/**
 * Stock Data Service
 * 
 * Centralized service for accessing stock items and warehouse locations.
 * Uses localStorage to ensure data consistency across tabs.
 */

import { StockItem } from "@/types/stock-item";
import { WarehouseLocation } from "@/types/warehouseLocation";
import { generateMockStockItems } from "@/data/mockStockItems";
import { getInitializedLocations, initializeLocationsWithStockData } from "@/data/initializeLocationStock";
import { 
  getStoredStockItems, 
  storeStockItems, 
  getStoredLocations, 
  storeLocations,
  hasStoredData 
} from "@/data/dataStore";

/**
 * Get stock items (from localStorage or generate new)
 */
export const getStockItems = (): StockItem[] => {
  // Try to get from localStorage first
  const stored = getStoredStockItems();
  if (stored) {
    return stored;
  }

  // Generate new items if not in storage
  const items = generateMockStockItems();
  storeStockItems(items);
  
  // Also initialize locations with this data
  initializeLocationsWithStockData(items);
  const locations = getInitializedLocations();
  storeLocations(locations);

  return items;
};

/**
 * Get warehouse locations (from localStorage or generate new)
 */
export const getWarehouseLocations = (): WarehouseLocation[] => {
  // Try to get from localStorage first
  const stored = getStoredLocations();
  if (stored) {
    return stored;
  }

  // If no stored locations, ensure stock items are generated first
  const stockItems = getStockItems();
  
  // Initialize locations with stock data
  initializeLocationsWithStockData(stockItems);
  const locations = getInitializedLocations();
  storeLocations(locations);

  return locations;
};

/**
 * Refresh all data (clear cache and regenerate)
 */
export const refreshAllData = (): void => {
  // Clear localStorage
  localStorage.clear();
  
  // Generate new data
  const items = generateMockStockItems();
  storeStockItems(items);
  
  initializeLocationsWithStockData(items);
  const locations = getInitializedLocations();
  storeLocations(locations);
};
